import React from 'react';
import { observer } from 'mobx-react';
import { Button, Divider } from 'antd';
import {
    AimOutlined,
    BankOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CreditCardOutlined,
    DeleteOutlined,
    DollarCircleOutlined,
    DownloadOutlined,
    EuroOutlined,
    ExclamationCircleOutlined,
    FieldTimeOutlined,
    FileTextOutlined,
    InfoCircleOutlined,
    InteractionOutlined,
    LinkOutlined,
    SafetyCertificateOutlined,
    SafetyOutlined,
    SendOutlined,
    SettingOutlined,
    UploadOutlined,
    UserOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { ShowCompanyInfoTenantConfig } from './ShowCompanyInfoTenantConfig';
import cx from 'classnames';
import { AppSystem, IShowForm, OrderBy } from '../../../interfaces';
import {
    useAppSystem,
    useCompanyConfig,
    useCompanyInfo,
    useCustomerPrivacyCheck,
    useIsMyCompany,
    useProfile,
    useProfileFeatureFlags,
    useProfileIsRight,
    useUiIsLoading,
} from '../../../hooks';
import { showMFAValue, updateCompanySubscribersMigrate } from '../../../middleware';
import { dateText2Utc, formatDataTestAttribute } from '../../../instruments';
import { ITrInterfaces, tB, tI, tP } from '../../../translate';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FieldInfoAdvanced,
    GoPageButton,
    LinkComponent,
    ShowBooleanEDField,
    cardLayoutOptions,
} from '../../../Components';
import Styles from '../../styles/m_itemCardStyles.less';

export const ShowCompanyInfo: React.FC<IShowForm> = observer(({ setEditMode }: IShowForm) => {
    const isLoading = useUiIsLoading();
    const isLord = useProfileIsRight('LORD');
    const isAdmin = useProfileIsRight('ADMIN');
    const { companyId } = useProfile();
    const {
        id: currentCompanyId,
        balance,
        cdrSquashIntervalName,
        cdrSquashIntervalValue,
        companyType,
        currency,
        customerPrivacy = true,
        deletedAt,
        externalId,
        geolocationEnabled,
        geotrackingEnabled,
        name,
        notes,
        parent,
        parentId,
        paymentTypeName,
        primaryContact,
        primaryContactId,
        privacyCdr,
        sessionGrantedTypeId,
        sessionGrantedUnit,
        sessionValidityTime,
        spendingLimit,
        subscribersAmount,
        subscribersOnPremAmount,
        thresholdHigh,
        thresholdMedium,
        zeroCostData,
        zeroCostSms,
        zeroCostVoice,
    } = useCompanyInfo();
    const { mfaForcedBy, mfa } = useCompanyConfig();
    const isCustomerPrivacyCheck = useCustomerPrivacyCheck(customerPrivacy);
    const isTopUp = useProfileFeatureFlags().topUp;

    const isMyCompany = useIsMyCompany();
    const appSystem = useAppSystem();
    const isBilling = appSystem === AppSystem.BILLING;

    const renderButton = () => {
        if (!isTopUp && isMyCompany) {
            return null;
        }

        if (!isAdmin || deletedAt) {
            return null;
        }

        return (
            <GoPageButton
                className={Styles.mainButton}
                componentName={'OrderNew'}
                disabled={isLoading}
                goToOptions={{ params: { id: currentCompanyId, by: OrderBy.COMPANY } }}
                href={`/company/${currentCompanyId}/order/${OrderBy.COMPANY}`}
                title={!isMyCompany ? 'Give Credits' : 'Top Up'}
            />
        );
    };

    const layout = {
        labelCol: {
            flex: '0 1 190px',
        },
        rowGutter: 8,
    };

    const onClickSubscribersMigrate = (onPerm: boolean) => {
        updateCompanySubscribersMigrate(onPerm);
    };

    return (
        <Catcher>
            <CardMainLayout
                columnOption={cardLayoutOptions.cardCompanyShowInfo}
                idSpinners={['GET_COMPANY', 'POST_COMPANY_SUBSCRIBERS_MIGRATE']}
                name={companyId === currentCompanyId ? tP('My Company') : name}
                dataTest={'ShowCompany'}
            >
                <FieldInfoAdvanced
                    icon={<BankOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Name'}
                    dataTest={'ShowCompanyName'}
                >
                    {name}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<BankOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Parent Company'}
                >
                    <LinkComponent
                        currentListComponent={isBilling ? 'BillingCompaniesList' : 'CompaniesList'}
                        id={parentId}
                        name={parent}
                    />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<SettingOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Type'}
                >
                    {companyType}
                </FieldInfoAdvanced>
                {externalId ? (
                    <FieldInfoAdvanced
                        icon={<LinkOutlined className={Styles.labelIcon} />}
                        layout={layout}
                        title={'External ID'}
                        dataTest={'ShowCompanyExternalId'}
                    >
                        {externalId}
                    </FieldInfoAdvanced>
                ) : null}
                <FieldInfoAdvanced
                    icon={<UserOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Primary contact'}
                >
                    {isCustomerPrivacyCheck ? (
                        '*****'
                    ) : (
                        <LinkComponent currentListComponent={'UsersList'} id={primaryContactId} name={primaryContact} />
                    )}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<DollarCircleOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Currency'}
                >
                    {currency}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<EuroOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Balance'}
                >
                    {balance}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<CreditCardOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Payment type'}
                >
                    {paymentTypeName}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    isIconBlank
                    layout={layout}
                    title={'Balance limit'}
                    titleSuffix={<InfoCircleOutlined />}
                >
                    {spendingLimit}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'1st balance warning'} titleSuffix={<InfoCircleOutlined />}>
                    {thresholdMedium}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'2nd balance warning'} titleSuffix={<InfoCircleOutlined />}>
                    {thresholdHigh}
                </FieldInfoAdvanced>
                {isLord ? (
                    <>
                        <Divider />
                        <FieldInfoAdvanced
                            icon={<ClockCircleOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'CDR interval'}
                        >
                            {cdrSquashIntervalValue}{' '}
                            {cdrSquashIntervalName ? tI(cdrSquashIntervalName as ITrInterfaces) : null}
                        </FieldInfoAdvanced>
                        <Divider />
                        <FieldInfoAdvanced
                            icon={<ExclamationCircleOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'Zero cost Data'}
                            dataTest={'ShowCompanyZeroCostDataValue'}
                        >
                            <ShowBooleanEDField field={zeroCostData} />
                        </FieldInfoAdvanced>
                        <FieldInfoAdvanced
                            icon={<ExclamationCircleOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'Zero cost SMS'}
                            dataTest={'ShowCompanyZeroCostSmsValue'}
                        >
                            <ShowBooleanEDField field={zeroCostSms} />
                        </FieldInfoAdvanced>
                        <FieldInfoAdvanced
                            icon={<ExclamationCircleOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'Zero cost Voice'}
                            dataTest={'ShowCompanyZeroCostVoice'}
                        >
                            <ShowBooleanEDField field={zeroCostVoice} />
                        </FieldInfoAdvanced>
                        <Divider />
                    </>
                ) : null}
                {isLord ? (
                    <>
                        <FieldInfoAdvanced
                            icon={<LinkOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'Subscriber location'}
                        />
                        <FieldInfoAdvanced
                            icon={<UploadOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'This OCS'}
                        >
                            {subscribersAmount}
                        </FieldInfoAdvanced>
                        <FieldInfoAdvanced
                            icon={<DownloadOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'On-prem OCS'}
                        >
                            {subscribersOnPremAmount}
                        </FieldInfoAdvanced>
                        <CardInfoToolBar isDisabledSave isHideBack>
                            <Button
                                className={Styles.defaultButton}
                                disabled={isLoading || !subscribersOnPremAmount}
                                icon={<UploadOutlined className={Styles.labelIcon} />}
                                loading={isLoading}
                                onClick={() => onClickSubscribersMigrate(false)}
                                type="primary"
                            >
                                {tB('Move to this OCS')}
                            </Button>
                            <Button
                                className={Styles.defaultButton}
                                disabled={isLoading || subscribersAmount === subscribersOnPremAmount}
                                icon={<DownloadOutlined className={Styles.labelIcon} />}
                                loading={isLoading}
                                onClick={() => onClickSubscribersMigrate(true)}
                                type="primary"
                            >
                                {tB('Move to on-prem OCS')}
                            </Button>
                        </CardInfoToolBar>

                        <Divider />
                    </>
                ) : null}
                {isAdmin ? (
                    <FieldInfoAdvanced
                        icon={<SafetyOutlined className={Styles.labelIcon} />}
                        layout={layout}
                        title={'CDR Privacy'}
                    >
                        {privacyCdr ? (
                            <CheckCircleOutlined
                                data-test={formatDataTestAttribute('ShowCompany', 'CheckCircleOutlined', 'Icon')}
                                title={tB('Enable')}
                            />
                        ) : (
                            <WarningOutlined
                                data-test={formatDataTestAttribute('ShowCompany', 'WarningOutlined', 'Icon')}
                                title={tB('Disable')}
                            />
                        )}
                    </FieldInfoAdvanced>
                ) : null}
                <FieldInfoAdvanced
                    icon={<FieldTimeOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Session Validity Time'}
                >
                    {sessionValidityTime}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<InteractionOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Session Granted Unit'}
                >
                    {sessionGrantedUnit}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<SendOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Session Granted Type'}
                    dataTest={'ShowCompanySessionGrantedType'}
                >
                    {sessionGrantedTypeId //:TODO investigate session granted type
                        ? sessionGrantedTypeId === '1'
                            ? currency
                            : sessionGrantedTypeId === '2'
                            ? 'MB'
                            : '???'
                        : null}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<AimOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Geolocation'}
                    dataTest={'ShowCompanyGeolocation'}
                >
                    <ShowBooleanEDField field={geolocationEnabled} />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<AimOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'Geotracking'}
                    dataTest={'ShowCompanyGeotracking'}
                >
                    <ShowBooleanEDField field={geotrackingEnabled} />
                </FieldInfoAdvanced>
                {isAdmin ? (
                    <>
                        <FieldInfoAdvanced
                            icon={<FileTextOutlined className={Styles.labelIcon} />}
                            layout={layout}
                            title={'Notes'}
                        >
                            {notes}
                        </FieldInfoAdvanced>
                    </>
                ) : null}
                {isAdmin && deletedAt ? (
                    <>
                        <FieldInfoAdvanced
                            icon={<DeleteOutlined className={cx(Styles.labelIcon, Styles.redIcon)} />}
                            layout={layout}
                            title={'Is deleted'}
                            dataTest={'ShowCompanyIsDeleted'}
                        >
                            {dateText2Utc(deletedAt)}
                        </FieldInfoAdvanced>
                    </>
                ) : null}

                <FieldInfoAdvanced
                    icon={<SafetyCertificateOutlined className={Styles.labelIcon} />}
                    layout={layout}
                    title={'2-Step Authentication Enforcement'}
                >
                    {showMFAValue(mfa, mfaForcedBy)}
                </FieldInfoAdvanced>

                <ShowCompanyInfoTenantConfig />

                <CardInfoToolBar setEditMode={!deletedAt ? setEditMode : undefined}>{renderButton()}</CardInfoToolBar>
            </CardMainLayout>
        </Catcher>
    );
});
